<script setup lang="ts">
import {
  LEVEL_XP,
  MAX_LEVEL,
  compactXp,
  displayFrom,
  hexAlpha,
  type LevelTier,
} from '@/utils/levels';

// Pitch + fatos do elo em foco. Este bloco NÃO vive mais no fluxo da página:
// é o conteúdo do dropdown de info do pódio (o ⓘ ao lado do título do elo) —
// pedido do produto pra tela apresentar só o essencial e guardar os detalhes
// atrás de um clique.
const props = defineProps<{
  tier: LevelTier;
  /** Faixa de XP do elo, já formatada ("2 Milhões — 5 Milhões XP"). */
  xpRange: string;
  /**
   * Medidas enxutas pro painel de info do pódio (380px): os três quadrinhos
   * têm que caber numa linha só.
   */
  compact?: boolean;
}>();

/**
 * Quanto XP separa o degrau VIP do seguinte na régua — cada elo VIP é um nível
 * só, então "XP para o próximo nível" é a distância da entrada dele até a
 * entrada do próximo. O VIP 6 é o teto da escala e não tem próximo.
 */
const vipXpToNext = computed(() => {
  const level = props.tier.from;
  const entry = LEVEL_XP[level];
  const next = LEVEL_XP[level + 1];

  if (level >= MAX_LEVEL || entry === undefined || next === undefined) {
    return 'Topo da escala';
  }

  return `${compactXp(next - entry)} XP`;
});

/**
 * Os três fatos do elo. O terceiro é sempre o benefício-vitrine (`topBenefit`,
 * curado a dedo na tabela de elos) — o cashback saiu de cena a pedido do
 * produto. No VIP a faixa de níveis vira o nível único e a distância pro
 * próximo degrau.
 */
const facts = computed(() => {
  const tier = props.tier;
  const topBenefit = {
    label: 'Maior benefício',
    value: tier.topBenefit,
    shine: tier.topBenefitShine ?? false,
  };

  if (tier.isVip) {
    // O VIP 6 é o teto: em vez de "quanto falta", o card anuncia "Em breve" —
    // a régua acima dele ainda não existe.
    const isCeiling = tier.from >= MAX_LEVEL;

    return [
      { label: 'Nível', value: `${tier.from}`, shine: false },
      isCeiling
        ? { label: 'Próximo nível', value: 'Em breve', shine: false }
        : {
            label: 'XP para o próximo nível',
            value: vipXpToNext.value,
            shine: false,
          },
      topBenefit,
    ];
  }

  // `displayFrom`: o Bronze se apresenta como 1–4 — o nível 0 é o estado
  // "sem elo" e não entra na vitrine de elo nenhum.
  const from = displayFrom(tier);
  const span = tier.to - from + 1;

  return [
    {
      label: span === 1 ? 'Nível' : 'Níveis',
      value: `${from}–${tier.to}`,
      shine: false,
    },
    { label: 'XP no elo', value: props.xpRange, shine: false },
    topBenefit,
  ];
});
</script>

<template>
  <div class="flex flex-col items-center gap-3.5">
    <!-- No compacto o painel é `w-max` e quem dita a largura é a fileira de
         fatos; o pitch entra com `w-0 min-w-full` pra NÃO alargar o painel e
         quebrar linha dentro da largura que os 3 cards definiram. No "Sem elo"
         não há fatos pra dar largura — o pitch ganha uma fixa, senão o painel
         colapsa. -->
    <p
      class="text-center text-[14px] font-normal leading-[21px] text-white/70"
      :class="
        compact
          ? tier.isUnranked
            ? 'w-[280px] max-w-full'
            : 'w-0 min-w-full'
          : 'max-w-[520px]'
      "
    >
      {{ tier.pitch }}
    </p>

    <!-- No compacto (painel do pódio) os três ficam SEMPRE numa linha, cada
         um na largura natural do texto completo — o painel é `w-max` e cresce
         junto. No largo, a fileira quebra se faltar espaço. -->
    <div
      v-if="!tier.isUnranked"
      class="flex items-stretch justify-center"
      :class="compact ? 'gap-1.5' : 'flex-wrap gap-2.5'"
    >
      <div
        v-for="fact in facts"
        :key="fact.label"
        class="flex flex-col rounded-xl"
        :class="
          compact
            ? 'flex-none gap-0.5 px-3 py-2'
            : 'min-w-[132px] gap-1 px-4 py-3'
        "
        :style="{
          background: `linear-gradient(160deg,${hexAlpha(tier.color, 0.14)},rgba(255,255,255,.02))`,
          boxShadow: `inset 0 0 0 1px ${hexAlpha(tier.color, 0.28)}`,
        }"
      >
        <span
          class="whitespace-nowrap font-semibold uppercase text-white/[0.55]"
          :class="
            compact
              ? 'text-[8.5px] leading-[12px] tracking-[0.08em]'
              : 'text-[10px] leading-[14px] tracking-[0.12em]'
          "
        >
          {{ fact.label }}
        </span>
        <!-- `nowrap` sempre: o texto sai completo e o card cresce pra caber. -->
        <span
          class="whitespace-nowrap font-bold text-white tabular-nums"
          :class="[
            compact
              ? 'text-[12px] leading-[16px]'
              : 'text-[14px] leading-[19px]',
            fact.shine ? 'levels-fact-shine' : '',
          ]"
        >
          {{ fact.value }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/*
  Shine do "Gerente pessoal" (VIP 4): uma faixa clara varre o texto de tempos
  em tempos. Keyframe (não transition) por causa do reset global do main.scss.
*/
@keyframes levelsFactShine {
  0%,
  55% {
    background-position: 130% 50%;
  }
  100% {
    background-position: -30% 50%;
  }
}

.levels-fact-shine {
  background: linear-gradient(100deg, #fff 42%, #ffe9a8 50%, #fff 58%);
  background-size: 220% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: levelsFactShine 2.8s ease-in-out infinite;
  filter: drop-shadow(0 0 8px rgba(255, 224, 138, 0.35));
}
</style>
